// ============================================================================
// Private farm-document URL consumer registry
// ============================================================================
//
// PrivateStorageImage is a shared visual sidecar. Its automatic signed URLs
// are not route resources: the caller's surface can still have legacy data
// reads, and a scoped image URL must never be mistaken for permanent route
// ownership or a boot-release claim. This finite registry receipts every JSX
// mount that automatically renders one of the private farm-document images.
// ============================================================================

/** The one authenticated Query resource used by automatic document images. */
export const PRIVATE_STORAGE_URL_RESOURCE = Object.freeze({
  SIGNED_URL: 'shared.private-storage.signed-url',
});

export const PRIVATE_STORAGE_IMAGE_MOUNT = Object.freeze({
  FLEET_FUELING_HISTORY: 'fleet-fueling-history',
  FLEET_MAINTENANCE_HISTORY: 'fleet-maintenance-history',
  FLEET_PHOTO_LIGHTBOX: 'fleet-photo-lightbox',
  FLEET_MAINTENANCE_EDITOR: 'fleet-maintenance-editor',
  CHECKLIST_ENTRY: 'checklist-entry',
  FUELING_ENTRY: 'fueling-entry',
  FUELING_FORM: 'fueling-form',
});

export type PrivateStorageImageMount = (typeof PRIVATE_STORAGE_IMAGE_MOUNT)[keyof typeof PRIVATE_STORAGE_IMAGE_MOUNT];

export type PrivateStorageImageConsumer = {
  readonly source: string;
  readonly surface: string;
  readonly mount: PrivateStorageImageMount;
};

// One registration per PrivateStorageImage JSX mount. `surface` identifies
// where a shared image is reachable, not a route-data or boot owner. The three
// EquipmentDetail entries are deliberately separate because their mount
// conditions differ: visible history rows vs. a user-opened lightbox.
export const PRIVATE_STORAGE_IMAGE_CONSUMERS: readonly PrivateStorageImageConsumer[] = Object.freeze([
  Object.freeze({
    source: 'src/equipment/EquipmentChecklistEntryPage.tsx',
    surface: 'equipment.checklist.entry',
    mount: PRIVATE_STORAGE_IMAGE_MOUNT.CHECKLIST_ENTRY,
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentDetail.tsx',
    surface: 'equipment.fleet.detail',
    mount: PRIVATE_STORAGE_IMAGE_MOUNT.FLEET_FUELING_HISTORY,
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentDetail.tsx',
    surface: 'equipment.fleet.detail',
    mount: PRIVATE_STORAGE_IMAGE_MOUNT.FLEET_MAINTENANCE_HISTORY,
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentDetail.tsx',
    surface: 'equipment.fleet.detail',
    mount: PRIVATE_STORAGE_IMAGE_MOUNT.FLEET_PHOTO_LIGHTBOX,
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentFuelingEntryPage.tsx',
    surface: 'equipment.fueling.entry',
    mount: PRIVATE_STORAGE_IMAGE_MOUNT.FUELING_ENTRY,
  }),
  Object.freeze({
    source: 'src/equipment/EquipmentMaintenanceModal.tsx',
    surface: 'equipment.fleet.detail',
    mount: PRIVATE_STORAGE_IMAGE_MOUNT.FLEET_MAINTENANCE_EDITOR,
  }),
  Object.freeze({
    source: 'src/webforms/EquipmentFuelingWebform.tsx',
    surface: 'equipment.fueling.form',
    mount: PRIVATE_STORAGE_IMAGE_MOUNT.FUELING_FORM,
  }),
]);
